import React, { useEffect, useState } from 'react';
import { Link2, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { apiCall } from '../utils/api';

export const WsafeSiSettingsCard = () => {
  const { toast } = useToast();
  const [cfg, setCfg] = useState(null);
  const [apiKey, setApiKey] = useState('');
  const [secret, setSecret] = useState('');
  const [siteId, setSiteId] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    apiCall('/api/wsafe-si/settings').then((d) => { setCfg(d); setSiteId(d.site_id || ''); }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = { site_id: siteId };
      if (apiKey) body.api_key = apiKey;
      if (secret) body.webhook_secret = secret;
      const d = await apiCall('/api/wsafe-si/settings', { method: 'PUT', body });
      setCfg(d); setApiKey(''); setSecret('');
      toast({ title: 'Saved', description: 'WSafe.SI connection updated.' });
    } catch (e) {
      toast({ title: 'Could not save', description: e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true); setResult(null);
    try { setResult(await apiCall('/api/wsafe-si/test-connection', { method: 'POST' })); }
    catch (e) { setResult({ ok: false, message: e.message }); }
    finally { setTesting(false); }
  };

  return (
    <Card data-testid="wsafe-si-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Link2 className="w-5 h-5" /> WSafe.SI connection</CardTitle>
        <CardDescription>Detections go to the WSafe.SI review queue. A competent person confirms or rejects each one there, and HazardLoop opens the ticket.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-sm text-slate-600" data-testid="wsafe-si-status">
          Status: <strong>{cfg?.connected ? 'API key saved' : 'Not connected'}</strong>
          {cfg?.api_key_masked && <span className="ml-2 font-mono">{cfg.api_key_masked}</span>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="wsafe-api-key">Org API key</Label>
          <Input id="wsafe-api-key" type="password" placeholder={cfg?.api_key_masked ? 'Enter a new key to replace' : 'Paste your WSafe.SI org API key'} value={apiKey} onChange={(e) => setApiKey(e.target.value)} data-testid="wsafe-si-api-key-input" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="wsafe-secret">Webhook signing secret</Label>
          <Input id="wsafe-secret" type="password" placeholder={cfg?.webhook_secret_configured ? 'Saved. Enter a new secret to replace' : 'From WSafe.SI webhook settings'} value={secret} onChange={(e) => setSecret(e.target.value)} data-testid="wsafe-si-webhook-secret-input" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="wsafe-site">Default site ID</Label>
          <Input id="wsafe-site" value={siteId} onChange={(e) => setSiteId(e.target.value)} data-testid="wsafe-si-site-id-input" />
        </div>
        {cfg?.webhook_url && (
          <p className="text-xs text-slate-500" data-testid="wsafe-si-webhook-url">Webhook URL for WSafe.SI: <span className="font-mono break-all">{cfg.webhook_url}</span></p>
        )}
        <div className="flex gap-2">
          <Button onClick={save} disabled={saving} data-testid="wsafe-si-save-btn">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save</Button>
          <Button variant="outline" onClick={test} disabled={testing} data-testid="wsafe-si-test-btn">{testing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Test connection</Button>
        </div>
        {result && (
          <div className={`flex items-center gap-2 text-sm ${result.ok ? 'text-green-700' : 'text-red-700'}`} data-testid="wsafe-si-test-result">
            {result.ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}{result.message}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default WsafeSiSettingsCard;
